import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Clock, Phone, Mail, MessageSquare, CheckCircle, TrendingUp, Plus, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const ActivityTimeline = ({ entityType, entityId }) => {
  const [activities, setActivities] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    activity_type: 'call',
    title: '',
    description: '',
  });

  useEffect(() => {
    fetchActivities();
  }, [entityType, entityId]);

  const fetchActivities = async () => {
    try {
      const response = await api.get('/activities', {
        params: { entity_type: entityType, entity_id: entityId, limit: 50 }
      });
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/activities', {
        entity_type: entityType,
        entity_id: entityId,
        ...formData,
      });
      toast.success('Activity logged successfully');
      setDialogOpen(false);
      fetchActivities();
      resetForm();
    } catch (error) {
      console.error('Error creating activity:', error);
      toast.error('Failed to log activity');
    }
  };

  const resetForm = () => {
    setFormData({
      activity_type: 'call',
      title: '',
      description: '',
    });
  };

  const getActivityIcon = (type) => {
    const icons = {
      call: <Phone className="h-4 w-4" />,
      meeting: <MessageSquare className="h-4 w-4" />,
      email: <Mail className="h-4 w-4" />,
      note: <FileText className="h-4 w-4" />,
      status_change: <TrendingUp className="h-4 w-4" />,
      task_completed: <CheckCircle className="h-4 w-4" />,
      proposal_sent: <FileText className="h-4 w-4" />,
      proposal_accepted: <CheckCircle className="h-4 w-4" />,
    };
    return icons[type] || <Clock className="h-4 w-4" />;
  };

  const getActivityColor = (type) => {
    const colors = {
      call: 'bg-blue-100 text-blue-800',
      meeting: 'bg-purple-100 text-purple-800',
      email: 'bg-green-100 text-green-800',
      note: 'bg-gray-100 text-gray-800',
      status_change: 'bg-orange-100 text-orange-800',
      task_completed: 'bg-green-100 text-green-800',
      proposal_sent: 'bg-yellow-100 text-yellow-800',
      proposal_accepted: 'bg-green-100 text-green-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex justify-between items-center">
          <CardTitle className="text-lg">Activity Timeline</CardTitle>
          <Button size="sm" onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-1" />
            Log Activity
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Clock className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            <p>No activities yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activities.map((activity) => (
              <div key={activity.id} className="flex gap-3 pb-4 border-b border-slate-100 last:border-0">
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${getActivityColor(activity.activity_type)}`}>
                  {getActivityIcon(activity.activity_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-medium text-sm text-slate-900">{activity.title}</h4>
                    <span className="text-xs text-slate-500 whitespace-nowrap">{formatDate(activity.created_at)}</span>
                  </div>
                  {activity.description && (
                    <p className="text-sm text-slate-600 mt-1">{activity.description}</p>
                  )}
                  <Badge variant="outline" className="text-xs mt-2">
                    {activity.activity_type.replace('_', ' ')}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Log Activity Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Log Activity</DialogTitle>
              <DialogDescription>Record a call, meeting, email, or note</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="activity_type">Activity Type *</Label>
                <Select
                  value={formData.activity_type}
                  onValueChange={(value) => setFormData({ ...formData, activity_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Phone Call</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="note">Note</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="e.g., Discussed project timeline"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Details</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Add details about this activity..."
                  rows={3}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Log Activity
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ActivityTimeline;
